import React, { useEffect } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation, useParams } from "react-router-dom";

import { trackPhlebotomyPageView } from "../../services/PhlebotomyTrackingService";
import {
  PHLEBOTOMY_INTAKE_URL,
  PHLEBOTOMY_LEGACY_HUB_URL,
  PHLEBOTOMY_SEARCH_URL,
} from "../../utils/urlConstants";

interface LegacyPhlebotomyParams {
  legacyAccountId: string;
  userId: string;
}

/**
 * Keeps previously delivered EMR/portal links working while moving patients off
 * the retired clinic routes and into the clinic-aware finder.
 *
 * Only eight-character alphanumeric public IDs carry clinic scope. Legacy LIMS
 * values are discarded without a lookup and fall through to the universal
 * experience.
 */
const LegacyPhlebotomyRedirectPage = (): React.ReactElement => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { legacyAccountId, userId } = useParams<LegacyPhlebotomyParams>();

  useEffect(() => {
    let legacyPageName:
      | "PhlebotomyPage"
      | "PhlebotomyLocationFinderPage"
      | "PhlebotomyPhoneOptionsPage";
    if (location.pathname.includes("finder")) {
      legacyPageName = "PhlebotomyLocationFinderPage";
    } else if (location.pathname.includes("phone-options")) {
      legacyPageName = "PhlebotomyPhoneOptionsPage";
    } else {
      legacyPageName = "PhlebotomyPage";
    }

    trackPhlebotomyPageView(legacyPageName, undefined, userId).catch(
      () => undefined,
    );

    const redirectToCanonicalExperience = () => {
      const query = new URLSearchParams(location.search);
      query.delete("clinic");
      query.delete("lims_id");

      const isLegacyPaperQr =
        userId === "paper" &&
        location.pathname.startsWith(`${PHLEBOTOMY_LEGACY_HUB_URL}/`);
      if (isLegacyPaperQr) {
        // Paper QR codes are reminder intake links, not already-enrolled
        // Customer.io links. Do not let stale tracking parameters skip intake.
        query.delete("tracking_id");
        query.delete("user_id");
      } else {
        query.set("user_id", userId);
      }

      if (/^[A-Za-z0-9]{8}$/.test(legacyAccountId)) {
        query.set("clinic", legacyAccountId);
      }

      history.replace({
        pathname: isLegacyPaperQr
          ? PHLEBOTOMY_INTAKE_URL
          : PHLEBOTOMY_SEARCH_URL,
        search: query.toString() ? `?${query.toString()}` : "",
      });
    };

    redirectToCanonicalExperience();
  }, [history, legacyAccountId, location.pathname, location.search, userId]);

  return (
    <Box
      role="status"
      aria-label={t("phlebotomySearch.status.loading")}
      sx={{
        alignItems: "center",
        display: "flex",
        justifyContent: "center",
        minHeight: "100vh",
      }}
    >
      <CircularProgress size={32} />
    </Box>
  );
};

export default LegacyPhlebotomyRedirectPage;
